import { Home, MessageCircle, Newspaper, User, UsersRound } from "lucide-react";
import picomatch from "picomatch";
import {
  featureLabelMap,
  LAST_FEATURE_KEY,
} from "@/components/landing-page/landing-page.constants";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { announceTabReselectAtTop } from "./bottom-navbar-state";

/**
 * The app's primary destinations. One list for both navigation surfaces: the
 * bottom tab bar (phones) and the side rail (`md` and up — desktop, tablets,
 * iPhone Duo's inner display), so the two can never disagree on order, labels
 * or which tab a path belongs to.
 */
export const navItems = [
  {
    href: "/hiives",
    label: "Home",
    icon: Home,
    patterns: ["/hiives", "/hiives/**"],
  },
  {
    href: "/chat",
    label: "Chats",
    icon: MessageCircle,
    patterns: ["/chat", "/chat/**"],
  },
  {
    href: "/feed",
    label: "Feed",
    icon: Newspaper,
    patterns: ["/feed"],
  },
  {
    href: "/discover",
    label: "Community",
    icon: UsersRound,
    patterns: ["/discover"],
  },
  {
    href: "/profile",
    label: "Profile",
    icon: User,
    patterns: ["/profile", "/profile/**"],
  },
];

export type PrimaryNavItem = (typeof navItems)[number];

const matchers = navItems.map((item) => ({
  ...item,
  matcher: item.patterns.map((pattern) => picomatch(pattern)),
}));

export function findNavIndex(pathname: string) {
  const index = matchers.findIndex((item) =>
    item.matcher.some((match) => match(pathname)),
  );
  return index === -1 ? null : index;
}

/** Below this the page already counts as "at the top". */
const AT_TOP_THRESHOLD_PX = 4;

export function isPlainPrimaryClick(event: React.MouseEvent) {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Re-tapping the tab you are on, at its root page (iOS convention): scroll the
 * page to the top, or — if it is already there — let a page with an existing
 * refresh run it.
 *
 * Every tab root scrolls the DOCUMENT (the shell is `min-h-dvh`; RouteScroll-
 * Restorer and the bar's own collapse both read `window.scrollY`), so the
 * window is the visible page's scroller. Routes Next keeps mounted in hidden
 * <Activity> boundaries are `display: none` and own no scroll of their own,
 * so there is no stale scroller to hit by mistake. A restore still in flight
 * on this page has already stepped aside — it aborts on the `pointerdown`
 * that started this tap.
 */
export function scrollTabRootToTop(href: string) {
  if (window.scrollY <= AT_TOP_THRESHOLD_PX) {
    announceTabReselectAtTop(href);
    return;
  }
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? "instant" : "smooth" });
}

export function recordPrimaryNavigation(
  targetItem: PrimaryNavItem,
  surface: "bottom_bar" | "side_rail",
) {
  // Telemetry and the last-feature hint must never be able to abort a Link
  // click, especially in iOS private/PWA storage modes.
  try {
    captureClientEvent(ANALYTICS_EVENTS.BOTTOM_NAV_ITEM_CLICKED, {
      label: targetItem.label,
      href: targetItem.href,
      surface,
    });
    const label = featureLabelMap[targetItem.href];
    if (label) {
      localStorage.setItem(
        LAST_FEATURE_KEY,
        JSON.stringify({ path: targetItem.href, label }),
      );
    }
  } catch {
    // Navigation is the primary action; analytics/storage are best-effort.
  }
}
